if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.viewer) return;
      this.elements.viewer.querySelectorAll('video').forEach((video) => {
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        this.prepareGalleryVideo(video);
        video.removeAttribute('autoplay');
        video.pause();
      });

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      const initialMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
      if (initialMedia) this.playActiveMedia(initialMedia);
    }

    onSlideChanged(event) {
      const activeMedia = event.detail.currentElement;
      if (!activeMedia) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.toggle('is-active', element === activeMedia);
      });
      const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${ activeMedia.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      this.playActiveMedia(activeMedia);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      if (!activeMedia) return;
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    prepareGalleryVideo(video) {
      video.controls = false;
      video.removeAttribute('controls');
      if (video.dataset.tapPlaybackReady) return;
      video.dataset.tapPlaybackReady = 'true';
      video.tabIndex = 0;
      video.setAttribute('role', 'button');
      const updateState = () => video.setAttribute('aria-pressed', String(!video.paused));
      video.addEventListener('play', updateState);
      video.addEventListener('pause', updateState);
      updateState();
      let start = null;
      let dragged = false;
      video.addEventListener('pointerdown', (event) => {
        start = { x: event.clientX, y: event.clientY };
        dragged = false;
      }, { passive: true });
      video.addEventListener('pointermove', (event) => {
        if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 10) {
          dragged = true;
        }
      }, { passive: true });
      video.addEventListener('pointercancel', () => {
        dragged = true;
        start = null;
      }, { passive: true });
      const togglePlayback = () => {
        if (video.paused) {
          const playback = video.play();
          if (playback) playback.catch(() => {});
        } else {
          video.pause();
        }
      };
      video.addEventListener('click', () => {
        if (!dragged) togglePlayback();
        start = null;
      });
      video.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        togglePlayback();
      });
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (!deferredMedia) return;
      if (!deferredMedia.querySelector('video, iframe, model-viewer')) {
        deferredMedia.loadContent(false);
      }
      const video = deferredMedia.querySelector('video');
      if (video) {
        this.prepareGalleryVideo(video);
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('loop', '');
        video.setAttribute('playsinline', '');
        const playback = video.play();
        if (playback) playback.catch(() => {});
        return;
      }
      const iframe = deferredMedia.querySelector('iframe');
      if (iframe && (iframe.classList.contains('js-youtube') || iframe.classList.contains('js-vimeo'))) {
        const url = new URL(iframe.src);
        url.searchParams.set('autoplay', '1');
        url.searchParams.set('loop', '1');
        if (iframe.classList.contains('js-youtube')) {
          url.searchParams.set('mute', '1');
          url.searchParams.set('playlist', url.pathname.split('/').pop());
        } else {
          url.searchParams.set('muted', '1');
        }
        if (iframe.src !== url.toString()) iframe.src = url.toString();
        iframe.contentWindow?.postMessage(
          iframe.classList.contains('js-youtube')
            ? JSON.stringify({ event: 'command', func: 'playVideo', args: [] })
            : JSON.stringify({ method: 'play' }),
          url.origin
        );
      }
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
